/**
 * Copyright (c) Qwoach LLC 2020. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Artem Bogush on 2/9/20
 *
 */
import {Type} from "class-transformer";
import {classToClass} from "@qwoach/class-transformer-wrapper";
import dayjs from "dayjs";
import {EventQuestion} from "@/core/js/model/scheduler/EventQuestion";
import {LocationItem} from "@/core/js/model/scheduler/LocationItem";
import ParticipantItem from "@/core/js/model/scheduler/ParticipantItem";
import RecurrenceItem from "@shared/Recurrence/RecurrenceItem";
import {EmailReminderItem} from "@shared/scheduler/reminders/reminderItem-imports";
import {userTimezoneName} from "@/utils/helpers";
import {SessionConfirmationStatus} from "@shared/Program/ProgramStep/helpers/ProgramStepEnums";
import CoachingSessionStep from "@shared/Program/ProgramStep/CoachingSessionStep";
import FormItem from "@/core/modules/coach/Resources/Forms/model/FormItem";
import SendingReminderWrapper from "@shared/scheduler/reminders/SendingReminderWrapper";
import {SessionChangeTypeEnum} from "@shared/scheduler/SessionChangeTypeEnum";
import TimeZoneItem from "@shared/Profile/TimeZoneItem.js";
import FilterableItem from "@shared/Filterable/FilterableItem.js";


export default class ScheduledEvent extends FilterableItem {

	name: string = ""
	description: string = null
	googleEventId: string = null
	googleMasterEventId: string = null
	outlook365EventId: string = null
	outlook365MasterEventId: string = null
	iCloudEventId: string = null
	iCloudMasterEventId: string = null
	startTimeFormer: number = 0;
	startTimeRecurringMaster: number = null;
	startTime: number = 0;
	duration: number = 0;
	bufferBeforeMs: number = 0;
	bufferAfterMs: number = 0;
	coachId: string = ""
	ownerId: string = null
	clientId: string = null
	teamMemberId: string = null
	coachName: string = ""
	coachEmail: string = ""
	coachTz: string = ""
	coachTzName: string = ""
	coachPictureUrl: string = ""
	@Type(() => LocationItem)
	location: LocationItem = null;
	eventTypeId: string = "";

	inviteeName: string = "";
	inviteeEmail: string = "";
	inviteeTz: string = "";
	inviteeTzName: string = "";

	isGroup: boolean = false

	confirmationUrl: string = "";
	confirmationStatus: SessionConfirmationStatus = null
	pendingStatusExpirationTs: number = null
	pendingBookingKey: string = null

	calendarId: string = null

	@Type(() => FormItem)
	pendingIntakeForm: FormItem = null;//only used for paid sessions during payment transaction to restore responses in case of failed payment
	skipTimeCollisionCheck: boolean = false

	intakeFormAnswerId: string = null
	intakeFormAnswerUrl: string = null

	@Type(() => FormItem)
	intakeFormAnswerItem: FormItem = null //only populated for rescheduling events


	@Type(() => ParticipantItem)
	participants: ParticipantItem[] = [];
	participantIds: string[] = [];
	@Type(() => EventQuestion)
	questions: EventQuestion[] = [];

	@Type(() => ScheduledEvent)
	formerEvent: ScheduledEvent = null;
	reasonForChange: string = ""
	changeType: string = ""

	eventColor: string = ""

	rescheduleLink: string = ""
	cancelLink: string = ""

	isCompleted: boolean = false;

	isEmailReminderEnabled: boolean = false

	@Type(() => EmailReminderItem)
	emailReminder: EmailReminderItem = null //deprecated

	@Type(() => EmailReminderItem)
	emailReminders: EmailReminderItem[] = []

	@Type(() => SendingReminderWrapper)
	sendingReminderWrapper: SendingReminderWrapper[] = []

	@Type(() => RecurrenceItem)
	recurrence: RecurrenceItem = null
	recurrenceIndex: number = null
	hasMoreRecurringEvents: boolean = null
	isMasterEvent: boolean = null
	notifyClient: boolean = false //this is only used to inform calendar api if notification needs to be sent to user. In the database it should always be "false"
	isConfirmedByClient: boolean = true

	programId: string = null
	stepId: string = null


	invoiceId: string = null
	paidSession: string = null
	price: string = null
	paymentTerms: string = null

	isBusy: boolean = true
	isAllDay: boolean = false

	isScheduledByCoach: boolean = null

	packageId: string = null
	sessionWrapperId: string = null



	constructor() {
		super();
		this.upgradeRemindersIfNeeded()
	}

	getCurrentSchemaVersion(){
		return 1
	}

	migrateSchema(oldVersion, newVersion) {
		super.migrateSchema(oldVersion, newVersion);
		if(oldVersion < 2) {
			//this migration was performed in the backend under migrateTeamFeature migration
		}

	}


	isRecurring(){
		return this.recurrence != null
	}

	isRecurringInstanceEvent(){
		return this.isRecurring() && !this.isMasterEvent
	}

	durationMinutes(){
		return this.duration / (60 * 1000)
	}

	setRescheduled(){
		this.changeType = SessionChangeTypeEnum.RESCHEDULE
	}
	setCancelled(){
		this.changeType = SessionChangeTypeEnum.CANCEL
	}

	endTime(){
		return this.startTime + this.duration
	}
	startMonth(){
		return new Date(this.startTime).getMonth()
	}
	endMonth(){
		return new Date(this.endTime()).getMonth()
	}

	startTimeMoment(){
		return dayjs(this.startTime)
	}

	startDateTimeFormattedGMT(){
		return dayjs(this.startTime).format("YYYY-MM-DD[T]HH:mm:ssZ")
	}
	// endDateTimeFormattedISO(){
	// 	return new Date(this.endTime()).toISOString()
	// }

	startDateFormatted(){
		return dayjs(new Date(this.startTime)).format("dddd, MMM DD");
	}
	endDateFormatted(){
		return dayjs(new Date(this.endTime())).format("dddd, MMM DD");
	}
	startTimeFormatted(){
		return dayjs(this.startTime).format("hh:mma");
	}
	endTimeFormatted(){
		return dayjs(this.endTime()).format("hh:mma");
	}

	addParticipantsAll(participantArray){
		(participantArray || []).forEach(participant => {
			this.addParticipant(participant)
		})
	}
	addParticipant(participant){
		let hasItem = this.participants.some(item => item.id === participant.id)

		if(!hasItem){
			this.participants.push(participant)
			this.participantIds.push(participant.id)
		}
	}
	findParticipantIndexByEmail(email: string){
		let participantIndex = this.participants.map(participant => participant.email).indexOf(email);
		return participantIndex
	}

	findParticipantByEmail(email: string){
		return this.participants.find(item => item.email == email)
	}
	updateParticipant(participant: ParticipantItem){
		let index = this.findParticipantIndexByEmail(participant.email)
		if(index >= 0){
			this.participants[index] = participant
		}
	}

	participantsResetNotificationSent(){
		this.participants.forEach(participant => participant.isNotificationSent = false)
	}
	participantsResetConfirmation(){
		this.participants.forEach(participant => participant.confirmation = SessionConfirmationStatus.PENDING)
	}


	dateTimeFormatted(){
		let selectedTimeMoment = dayjs(this.startTime)

		let duration = this.duration
		let startTime = selectedTimeMoment.format("hh:mma")
		let endTime = selectedTimeMoment.add(duration).format("hh:mma")
		let date = selectedTimeMoment.format("dddd, MMM DD, YYYY")
		return "" + startTime + " - " + endTime + ", " + date

	}

	isSessionCompleted(){
		return this.endTime() < new Date().getTime()
	}

	isSessionConfirmed(){
		return this.confirmationStatus === SessionConfirmationStatus.CONFIRMED
	}

	isSessionCancelled(){
		return this.confirmationStatus === SessionConfirmationStatus.CANCELLED
	}

	locationFormatted(){
		if(this.location){
			return this.location.locationFormatted()
		}
		return ""
	}

	isSessionConfirmPending(){
		return this.confirmationStatus === SessionConfirmationStatus.PENDING_CLIENT
			|| this.confirmationStatus === SessionConfirmationStatus.PENDING_COACH
	}

	isSessionConfirmPendingClient(){
		return this.confirmationStatus === SessionConfirmationStatus.PENDING_CLIENT
	}

	isSessionConfirmPendingCoach(){
		return this.confirmationStatus === SessionConfirmationStatus.PENDING_COACH
	}

	getOwnerId(){
		return this.ownerId || this.coachId
	}

	getTeamMemberId(){
		return this.teamMemberId || this.coachId
	}


	fillAnswersFromCallStep(sessionStep: CoachingSessionStep){
		this.location = classToClass(sessionStep.locationItem)
		this.startTime = sessionStep.confirmedTime
		this.confirmationStatus = sessionStep.confirmationStatus || this.confirmationStatus
		if(sessionStep.isRecurring){
			this.recurrence = classToClass(sessionStep.recurrence);
		} else {
			this.recurrence = null
		}
	}

	remindersResetScheduledIds(){
		this.emailReminders.forEach(emailReminder => emailReminder.resetScheduledIds())
	}

	upgradeRemindersIfNeeded(){
		// console.log("upgradeRemindersIfNeeded: ", this.emailReminder)
		if(this.emailReminder){
			this.emailReminder.title = "Email reminder 1"
			this.emailReminder.trigger.recalculateShiftParams()
			this.emailReminders = [this.emailReminder]
			this.emailReminder = null
		}
	}

	fillFromSessionType(sessionType, clientProfile){
		this.coachTz = sessionType.coachTz
		if(this.coachTz){
			this.coachTzName = userTimezoneName(sessionType.coachTz)
		}
		this.bufferBeforeMs = sessionType.eventBufferBefore
		this.bufferAfterMs = sessionType.eventBufferAfter
		this.isEmailReminderEnabled = sessionType.isEmailReminderEnabled
		this.name = sessionType.displayName
		this.eventTypeId = sessionType.id
		this.coachId = sessionType.coachId
		this.ownerId = sessionType.ownerId || sessionType.coachId
		if(sessionType.teamMemberId){
			this.teamMemberId = sessionType.teamMemberId
		}
		this.coachName = sessionType.coachName
		this.coachEmail = sessionType.coachEmail
		// this.coachPictureUrl = sessionType.coachPictureUrl
		// this.brandLogoUrl = sessionType.brandLogoUrl
		this.duration = sessionType.duration
		this.questions = classToClass(sessionType.questions);
		this.eventColor = sessionType.eventColor

		this.paidSession = sessionType.isPaidSession()
		this.price = sessionType.price
		this.paymentTerms = sessionType.paymentTerms
		this.priceFormatted = sessionType.priceFormatted()
		this.attachedWorkflowIds = sessionType.attachedWorkflowIds


		this.emailReminders = classToClass(sessionType.emailReminders);
		this.sendingReminderWrapper = classToClass(sessionType.sendingReminderWrapper);
		console.log("ScheduledEvent: this.emailReminders = ", this.emailReminders, ", sessionType.emailReminders = ", sessionType.emailReminders)

		if(clientProfile){
			this.setDataFromClientProfile(clientProfile)
		}
		this.setupCancelAndRescheduleLinks()

	}

	setDataFromClientProfile(clientProfile){
		if(clientProfile){
			this.clientId = clientProfile.id
			this.inviteeName = clientProfile.name
			this.inviteeEmail = clientProfile.email
			this.inviteeTz = clientProfile.getTimeZoneId()
			this.addParticipant(clientProfile.asParticipant())//new ParticipantItem(clientProfile.id, clientProfile.name, clientProfile.email, clientProfile.timezone))
		}
	}

	getAttachedFileRefListForReminder(reminder){
		console.log("EventType.getAttachedFileRefListForReminder: reminder = ", reminder, ", this.sendingReminderWrapper = ", this.sendingReminderWrapper)
		if(!reminder){
			return []
		}
		for(let reminderWrapper of this.sendingReminderWrapper){
			if(reminderWrapper.initialReminder
				&& reminderWrapper.initialReminder.id === reminder.id){
				return reminderWrapper.getAllFilesRefs()
			}
		}
		return []
	}

	setupCancelAndRescheduleLinks(){
		let domain = process.env.VUE_APP_DOMAIN
		if(!domain){
			throw new Error("setupCancelAndRescheduleLinks: domain is null")
		}
		if(!this.id){
			return console.error("setupCancelAndRescheduleLinks: event id is null")
		}

		this.rescheduleLink = `${domain}/reschedule/${this.coachId}/${this.id}`
		this.cancelLink = `${domain}/cancel/${this.coachId}/${this.id}`
	}

	getRescheduleRouteParams(){
		return {
			name: 'RescheduleMeetingPage',
			params:{
				coachId: this.coachId,
				scheduledEventId: this.id
			}
		}
	}


	getCancelRouteParams(){
		return {
			name: 'CancelMeetingPage',
			params:{
				coachId: this.coachId,
				scheduledEventId: this.id
			}
		}
	}

	generateProgramLink(domain){
		if(!this.programId){
			throw new Error("generateProgramLinkCoach: programId is null")
		}
		return `${domain}/app/coaching/processes/${this.programId}`
	}


	getQwoachMasterEventId(){
		return this.googleMasterEventId || this.outlook365MasterEventId || this.iCloudMasterEventId
	}

	createRecurringMasterEvent(){
		let masterEvent = classToClass(this)
		if(!masterEvent.isMasterEvent){
			masterEvent.isMasterEvent = true

			// WARNING don't reset recurrenceIndex here!
			// it's needed to keep reference of the current instance event
			// it's used in splitEvent-related logic to correctly calculate number of occurrences
			// in both client (SessionsCalendarSyncManager.getEventInstances) and server (BaseCalendarApi.splitEvent)
			// this.recurrenceIndex = null

			masterEvent.startTime = masterEvent.startTimeRecurringMaster || masterEvent.startTime

			masterEvent.googleEventId = masterEvent.googleMasterEventId
			masterEvent.outlook365EventId = masterEvent.outlook365MasterEventId
			masterEvent.iCloudEventId = masterEvent.iCloudMasterEventId

			masterEvent.googleMasterEventId = null
			masterEvent.outlook365MasterEventId = null
			masterEvent.iCloudMasterEventId = null

			masterEvent.startTimeRecurringMaster = null

		}

		return masterEvent
	}

	hasCalendarEventId(calendarEvent){
		let res = calendarEvent != null &&
			(this.googleEventId != null && this.googleEventId === calendarEvent.googleEventId
				|| this.outlook365EventId && this.outlook365EventId === calendarEvent.outlook365EventId
				|| this.iCloudEventId & this.iCloudEventId === calendarEvent.iCloudEventId)

		console.log("hasCalendarEventId: res = ", res)
		return res
	}

	resetCalendarIds(){
		this.outlook365EventId = null
		this.outlook365MasterEventId = null
		this.googleEventId = null
		this.googleMasterEventId = null
		this.iCloudEventId = null
		this.iCloudMasterEventId = null
	}

	getEventTitle(){
		if(this.isGroup){
			return `${this.name} with ${this.coachName}`
		}
		this.participants = this.participants || []
		let participantsCount = this.participants.length
		let inviteeName = participantsCount > 1 ? "" : (this.inviteeName || (this.participants[0] || {}).name)
		if(inviteeName) {
			return inviteeName + " and " + this.coachName + ": " + this.name;
		}
		return this.name;
	}

	isFirstSessionInSeries(){
		return this.recurrenceIndex === 1;//this.startTimeRecurringMaster != null && this.startTimeRecurringMaster == this.startTime
	}

	async getAllEnabledEmailReminders(dataManager){
		let eventItem = this
		let allReminders = []
		if (!eventItem.isSessionConfirmed() || !eventItem.isEmailReminderEnabled){
			return []
		}

		allReminders.push(...eventItem.emailReminders)
		for(let sendingReminderWrapper of eventItem.sendingReminderWrapper){
			if(sendingReminderWrapper.initialReminder){
				allReminders.push(sendingReminderWrapper.initialReminder)
			}
		}
		if(eventItem.attachedWorkflowIds && eventItem.attachedWorkflowIds.length > 0){
			let workflowPromises = eventItem.attachedWorkflowIds.map(workflowId => dataManager.getWorkflowItem(eventItem.coachId, workflowId))
			let workflowsArray = await Promise.all(workflowPromises)
			for(let workflowItem of workflowsArray){
				allReminders.push(...workflowItem.emailReminders)
			}
		}

		console.log("getAllEnabledEmailReminders: ", allReminders)

		return allReminders
	}

	createSessionReminderData(reminderItem, participant){
		let qwoachEvent = this

		if(!participant){
			participant = qwoachEvent.participants[0]
			if(participant && !participant.timeZoneItem && qwoachEvent.inviteeTz){
				participant.timeZoneItem = new TimeZoneItem(qwoachEvent.inviteeTz)
			}
		}

		let inviteeTz = participant ? participant.getTimeZoneId() : null

		let dateTime = this.getInviteeDate(qwoachEvent, participant)
		let inviteeTimezone = inviteeTz ? " (" + inviteeTz + ")" : ""

		let location = this.locationFormatted(qwoachEvent.location)



		let qwoachFileRefList = qwoachEvent.getAttachedFileRefListForReminder(reminderItem)
		let qwoachFileDataList = qwoachFileRefList.map(fileRef => fileRef.toNameAndLink(qwoachEvent.coachId, qwoachEvent.clientId))
		console.log("SchedulerEmail.createSessionReminderData: qwoachFileRefList = ", qwoachFileRefList, ", qwoachFileDataList = ", qwoachFileDataList)

		let data = {
			coachId: qwoachEvent.teamMemberId || qwoachEvent.coachId,
			ownerId: qwoachEvent.ownerId || qwoachEvent.coachId,
			replyTo: qwoachEvent.coachEmail,
			clientId: qwoachEvent.clientId,
			coach_full_name: qwoachEvent.coachName,
			client_full_name: qwoachEvent.inviteeName,
			attached_files: qwoachFileDataList,
			session_date: dateTime.format("dddd, MMM DD"),
			session_time: dateTime.format("hh:mma") + inviteeTimezone,
			session_name: qwoachEvent.name,
			session_location: location,

			// footer: {
			// 	contactName: qwoachEvent.coachName,
			// 	contactEmail: qwoachEvent.coachEmail,
			// }
		}

		console.log("SchedulerEmail.createSessionReminderData: data = ", data)

		return data
	}

	getInviteeDate(qwoachEvent, participant) {
		let date = dayjs(qwoachEvent.startTime)
		console.log("getInviteeDate: date = ", date)
		if (participant && participant.getTimeZoneId()) {
			date = date.tz(participant.getTimeZoneId())
			console.log("getInviteeDate: date with tz = ", date, ", inviteeTz = ", participant.getTimeZoneId())
		}
		return date
	}

	getCoachDate(qwoachEvent) {
		let date = dayjs(qwoachEvent.startTime)
		let coachTz = qwoachEvent.coachTz
		console.log("getCoachDate: date = ", date)
		if (coachTz) {
			date = date.tz(coachTz)
			console.log("getCoachDate: date with tz = ", date, ", coachTz = ", coachTz)
		}
		return date
	}

	equalablePropsExcluded() {
		return [
			...super.equalablePropsExcluded(),
			"formerEvent"
		];
	}

	moveToNewClient(newClientId){
		if(this.clientIdOld){
			return false
		}
		this.clientIdOld = this.clientId
		this.clientId = newClientId
		return true
	}
}
